import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../lib/api-client';
import type { TeamRepoScore, RepoExecution } from '@sdd-maturity/shared-types';
import { TERMINAL_STATES } from '../lib/scanStatus';

interface TeamScanProgressProps {
  teamNumber: string;
  teamName: string;
  expectedCount: number;
  scanStartTime: number;
  repoExecutions: RepoExecution[];
  onComplete: () => void;
}

interface RepoStatus {
  name: string;
  status: 'scanning' | 'done' | 'skipped' | 'failed';
  score?: number;
  level?: string;
  errorMessage?: string;
}

/**
 * One source for each live-scan status's color + label, replacing the two blocks
 * of hand-maintained `status===` branches with hardcoded color-token literals that
 * previously lived in the summary and the per-repo list (P3/FR-3). This is the
 * component-local scan-status union (`scanning | done | skipped | failed`), kept
 * distinct from the repo-classification discriminant per the spec. `done` renders
 * `{level} — {score}` rather than a static label, so its `label` is unused.
 */
const STATUS_STYLE: Record<RepoStatus['status'], { color: string; label: string }> = {
  scanning: { color: 'var(--genai-sem-text-muted)', label: 'Scanning...' },
  done: { color: 'var(--genai-sem-success)', label: 'Done' },
  skipped: { color: 'var(--genai-sem-level-l2, #f59e0b)', label: 'Skipped' },
  failed: { color: 'var(--genai-sem-error, #e96051)', label: 'Failed' },
};

const POLL_INTERVAL = 15_000;
const MAX_POLLS = 40; // ~10 minutes max

// Fire onComplete at most ONCE per scan, even across unmount/remount (#711). The
// persisted team-scan (TEAM_SCAN_KEY in DeliveryScanPanel) survives navigation,
// so a remount re-detects an already-finished scan and would re-fire onComplete
// → refetch → remount → loop. We record the scanStartTime we've already fired for
// (one key per team; a new scan has a new scanStartTime and fires again).
const COMPLETE_FIRED_KEY = 'sdd-maturity-team-scan-complete';
function firedScanStartTime(teamNumber: string): number | null {
  try {
    const v = localStorage.getItem(`${COMPLETE_FIRED_KEY}:${teamNumber}`);
    return v === null ? null : Number(v);
  } catch { return null; }
}
function markCompleteFired(teamNumber: string, scanStartTime: number): void {
  try { localStorage.setItem(`${COMPLETE_FIRED_KEY}:${teamNumber}`, String(scanStartTime)); } catch { /* noop */ }
}

export function TeamScanProgress({ teamNumber, teamName, expectedCount, scanStartTime, repoExecutions, onComplete }: TeamScanProgressProps): React.JSX.Element {
  const { token } = useAuth();
  const [completed, setCompleted] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [failed, setFailed] = useState(0);
  const [repos, setRepos] = useState<RepoStatus[]>([]);
  const [isDone, setIsDone] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollCount = useRef(0);
  // Cache terminal execution statuses so we don't re-poll AND still return them
  const cachedStatuses = useRef<Map<string, { status: string; errorMessage?: string }>>(new Map());

  // Build a map from repo name to execution ARN
  const arnByRepo = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const map = new Map<string, string>();
    for (const exec of repoExecutions) {
      map.set(exec.repoName, exec.executionArn);
    }
    arnByRepo.current = map;
  }, [repoExecutions]);

  const checkExecutionStatuses = useCallback(async (repoNames: string[]): Promise<Map<string, { status: string; errorMessage?: string }>> => {
    const results = new Map<string, { status: string; errorMessage?: string }>();
    if (!token) return results;

    const toCheck: string[] = [];
    for (const name of repoNames) {
      const arn = arnByRepo.current.get(name);
      if (!arn) continue;
      // Return cached terminal status without re-polling
      const cached = cachedStatuses.current.get(name);
      if (cached && TERMINAL_STATES.has(cached.status)) {
        results.set(name, cached);
      } else {
        toCheck.push(name);
      }
    }

    // Poll only non-terminal repos
    const checks = toCheck.map(async (name) => {
      const arn = arnByRepo.current.get(name)!;
      try {
        const scanStatus = await apiClient.getScanStatus(arn, token);
        const entry = { status: scanStatus.status, errorMessage: scanStatus.errorMessage };
        if (TERMINAL_STATES.has(scanStatus.status)) {
          cachedStatuses.current.set(name, entry);
        }
        results.set(name, entry);
      } catch {
        // Ignore individual failures — will retry next poll
      }
    });
    await Promise.all(checks);
    return results;
  }, [token]);

  useEffect(() => {
    if (!token || isDone) return undefined;

    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - scanStartTime) / 1000));
    }, 1000);

    async function doPoll() {
      pollCount.current += 1;
      try {
        const data = await apiClient.getTeamRepos(teamNumber, token!);
        const allRepos = data.repos ?? [];

        let doneCount = 0;
        let skipCount = 0;
        let failCount = 0;

        // First pass: check scores from DynamoDB
        const statuses: RepoStatus[] = allRepos.map((r) => {
          const isRecent = r.lastScanTimestamp && new Date(r.lastScanTimestamp).getTime() > scanStartTime - 60000;
          // The skipped/stale decision shared with the classifier (SKIPPED level or
          // stale framework). Kept local and NOT routed through repoStatus()'s
          // `inactive` status: that discriminant folds inaccessible/infra/maintenance
          // frameworks away from "inactive", which would drop a recent SKIPPED-level
          // special-framework repo out of the skipped count and stall the completion
          // trigger (AC-3.4). The isRecent gate is preserved unchanged.
          const isSkippedRepo = r.overallLevel === 'SKIPPED' || r.framework === 'stale';
          if (isSkippedRepo && isRecent) {
            skipCount++;
            return { name: r.repoName, status: 'skipped' as const };
          }
          if (isRecent && r.overallScore >= 0) {
            doneCount++;
            return { name: r.repoName, status: 'done' as const, score: r.overallScore, level: r.overallLevel };
          }
          return { name: r.repoName, status: 'scanning' as const };
        });

        // Second pass: for repos still "scanning", check execution ARNs for failures
        const scanningRepos = statuses.filter((r) => r.status === 'scanning').map((r) => r.name);
        if (scanningRepos.length > 0 && repoExecutions.length > 0) {
          const execStatuses = await checkExecutionStatuses(scanningRepos);
          for (const repo of statuses) {
            if (repo.status !== 'scanning') continue;
            const execStatus = execStatuses.get(repo.name);
            if (!execStatus) continue;
            if (execStatus.status === 'FAILED' || execStatus.status === 'TIMED_OUT' || execStatus.status === 'ABORTED') {
              repo.status = 'failed';
              repo.errorMessage = execStatus.errorMessage;
              failCount++;
            }
          }
        }

        setCompleted(doneCount);
        setSkipped(skipCount);
        setFailed(failCount);
        setRepos(statuses);

        const resolvedCount = doneCount + skipCount + failCount;
        const scanningCount = statuses.filter((r) => r.status === 'scanning').length;
        const elapsedMs = Date.now() - scanStartTime;
        if (resolvedCount >= expectedCount || scanningCount === 0 || pollCount.current >= MAX_POLLS || elapsedMs > 10 * 60 * 1000) {
          setIsDone(true);
          // Idempotent across remounts (#711): only notify the parent (→ refetch)
          // the first time this scan reaches terminal. A remount re-runs doPoll to
          // repopulate the completed panel, but must NOT re-fire onComplete.
          if (firedScanStartTime(teamNumber) !== scanStartTime) {
            markCompleteFired(teamNumber, scanStartTime);
            onComplete();
          }
        }
      } catch {
        // Silently retry on next poll
      }
    }

    const poll = setInterval(() => { void doPoll(); }, POLL_INTERVAL);

    // First poll immediately
    void doPoll();

    return () => { clearInterval(poll); clearInterval(timer); };
  }, [token, teamNumber, expectedCount, scanStartTime, isDone, onComplete, repoExecutions, checkExecutionStatuses]);

  const total = expectedCount;
  const progress = total > 0 ? Math.round(((completed + skipped + failed) / total) * 100) : 0;
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const allFailed = failed > 0 && completed === 0 && skipped === 0;

  return (
    <div className="card" data-testid="team-scan-progress">
      <div className="flex items-center justify-between mb-md">
        <h3 className="text-lg font-semibold">
          {isDone
            ? allFailed ? 'Scan Failed' : 'Scan Complete'
            : 'Scanning'} — {teamName}
        </h3>
        <span className="text-base text-muted">
          {minutes > 0 ? `${minutes}m ` : ''}{seconds}s
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        height: '6px',
        borderRadius: '3px',
        backgroundColor: 'var(--genai-sem-surface-raised, #1e293b)',
        marginBottom: '16px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          borderRadius: '3px',
          background: isDone
            ? allFailed
              ? 'var(--genai-sem-error, #e96051)'
              : 'var(--genai-sem-success)'
            : 'linear-gradient(90deg, var(--genai-sem-accent-border, #ff8210), var(--genai-sem-accent, #0c80df))',
          transition: 'width 0.5s ease',
        }} />
      </div>

      {/* Status summary */}
      <div className="flex gap-lg mb-md" style={{ fontSize: 'var(--genai-text-base)' }}>
        <span>
          <strong style={{ color: 'var(--genai-sem-accent)' }}>{completed}</strong>
          <span className="text-muted"> / {total} scanned</span>
        </span>
        {skipped > 0 && (
          <span>
            <strong style={{ color: STATUS_STYLE.skipped.color }}>{skipped}</strong>
            <span className="text-muted"> skipped</span>
          </span>
        )}
        {failed > 0 && (
          <span>
            <strong style={{ color: STATUS_STYLE.failed.color }}>{failed}</strong>
            <span className="text-muted"> failed</span>
          </span>
        )}
        <span style={{ color: 'var(--genai-sem-text-muted)' }}>
          {progress}%
        </span>
      </div>

      {/* Per-repo status list */}
      {repos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {repos.map((r) => (
            <div key={r.name} className="flex items-center justify-between" style={{
              padding: '6px 10px',
              borderRadius: '4px',
              backgroundColor: 'var(--genai-sem-surface-raised, #1e293b)',
              fontSize: '12px',
            }}>
              <span className="font-medium">{r.name}</span>
              <span>
                {r.status === 'done' && (
                  <span style={{ color: STATUS_STYLE.done.color }}>
                    {r.level} — {r.score?.toFixed(1)}
                  </span>
                )}
                {r.status === 'skipped' && (
                  <span style={{ color: STATUS_STYLE.skipped.color }}>{STATUS_STYLE.skipped.label}</span>
                )}
                {r.status === 'failed' && (
                  <span style={{ color: STATUS_STYLE.failed.color }} title={r.errorMessage}>
                    {STATUS_STYLE.failed.label}
                  </span>
                )}
                {r.status === 'scanning' && (
                  <span style={{ color: STATUS_STYLE.scanning.color }}>{STATUS_STYLE.scanning.label}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {!isDone && repos.length === 0 && (
        <p className="text-base text-muted">Waiting for first results... (polls every 15s)</p>
      )}
    </div>
  );
}
